import { useState, type ReactNode } from 'react';
import { api } from '../lib/api.js';
import { useQuery } from '../lib/useQuery.js';
import { useAuth } from '../lib/auth.js';
import { Page } from '../app/AppShell.js';
import { useToast } from '../components/Toast.js';
import { Modal } from '../components/Modal.js';
import { Loading, ErrorNote, EmptyState } from '../components/ui.js';
import { relativeTime } from '../lib/format.js';
import type { AlertChannel, AlertItem, AlertRule, AlertSeverity } from '../lib/types.js';

const SEV_CLASS: Record<AlertSeverity, string> = {
  info: 'flat',
  warning: 'warn',
  critical: 'bad',
};

export function AlertsScreen(): ReactNode {
  const { can } = useAuth();
  const manage = can('alerts.manage');
  const [tab, setTab] = useState<'incidents' | 'config'>('incidents');

  return (
    <Page
      title="Alerts"
      actions={
        <div className="row">
          <button
            className={`btn small ${tab === 'incidents' ? 'primary' : ''}`}
            onClick={() => setTab('incidents')}
          >
            Incidents
          </button>
          {manage ? (
            <button
              className={`btn small ${tab === 'config' ? 'primary' : ''}`}
              onClick={() => setTab('config')}
            >
              Rules &amp; channels
            </button>
          ) : null}
        </div>
      }
    >
      {tab === 'incidents' ? <Incidents manage={manage} /> : <Config />}
    </Page>
  );
}

function Incidents({ manage }: { manage: boolean }): ReactNode {
  const [state, setState] = useState('');
  const alerts = useQuery(() => api.alerts(state ? { state } : {}), [state], { pollMs: 20000 });
  const toast = useToast();

  async function act(a: AlertItem, action: 'ack' | 'resolve'): Promise<void> {
    try {
      await (action === 'ack' ? api.acknowledgeAlert(a.id) : api.resolveAlert(a.id));
      alerts.refetch();
    } catch (err) {
      toast.error(err);
    }
  }

  return (
    <>
      <div className="row" style={{ marginBottom: 12 }}>
        <select value={state} onChange={(e) => setState(e.target.value)}>
          <option value="">Open &amp; acknowledged</option>
          <option value="open">Open</option>
          <option value="acknowledged">Acknowledged</option>
          <option value="resolved">Resolved</option>
        </select>
      </div>

      {alerts.loading && !alerts.data ? <Loading what="alerts" /> : null}
      {alerts.error ? <ErrorNote error={alerts.error} /> : null}
      {alerts.data && alerts.data.data.length === 0 ? <EmptyState>No alerts. All clear.</EmptyState> : null}

      {alerts.data && alerts.data.data.length > 0 ? (
        <div className="card">
          <table className="grid">
            <thead>
              <tr>
                <th>Severity</th>
                <th>Summary</th>
                <th>Opened</th>
                <th>State</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {alerts.data.data.map((a) => (
                <tr key={a.id}>
                  <td>
                    <span className={`badge ${SEV_CLASS[a.severity]}`}>{a.severity}</span>
                  </td>
                  <td>{a.summary}</td>
                  <td className="muted small">{relativeTime(a.openedAt)}</td>
                  <td className="muted small">{a.state}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {manage && a.state !== 'resolved' ? (
                      <div className="row">
                        {a.state === 'open' ? (
                          <button className="btn small" onClick={() => void act(a, 'ack')}>
                            Ack
                          </button>
                        ) : null}
                        <button className="btn small" onClick={() => void act(a, 'resolve')}>
                          Resolve
                        </button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}

function Config(): ReactNode {
  const rules = useQuery(() => api.alertRules(), []);
  const channels = useQuery(() => api.alertChannels(), []);
  const toast = useToast();
  const [editRule, setEditRule] = useState<AlertRule | 'new' | null>(null);

  async function toggleRule(r: AlertRule): Promise<void> {
    try {
      await api.saveAlertRule(r.id, { ...r, enabled: !r.enabled });
      rules.refetch();
    } catch (err) {
      toast.error(err);
    }
  }

  async function testChannel(c: AlertChannel): Promise<void> {
    try {
      const res = await api.testAlertChannel(c.id);
      toast.push(res.ok ? 'Test sent' : `Failed: ${res.error ?? 'unknown'}`, res.ok ? 'success' : 'error');
    } catch (err) {
      toast.error(err);
    }
  }

  return (
    <div className="stack" style={{ gap: 20 }}>
      <section>
        <div className="row between" style={{ marginBottom: 8 }}>
          <strong className="small">Rules</strong>
          <button className="btn small primary" onClick={() => setEditRule('new')}>
            New rule
          </button>
        </div>
        {rules.loading && !rules.data ? <Loading /> : null}
        {rules.data ? (
          <div className="card">
            <table className="grid">
              <thead>
                <tr>
                  <th>Rule</th>
                  <th>Kind</th>
                  <th>Severity</th>
                  <th>Enabled</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rules.data.data.map((r) => (
                  <tr key={r.id}>
                    <td>{r.label}</td>
                    <td className="mono small">{r.kind}</td>
                    <td>{r.severity}</td>
                    <td>
                      <input type="checkbox" checked={r.enabled} onChange={() => void toggleRule(r)} />
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="row">
                        <button className="btn small" onClick={() => setEditRule(r)}>
                          Edit
                        </button>
                        <button
                          className="btn small danger"
                          onClick={() => {
                            if (!confirm(`Delete “${r.label}”?`)) return;
                            void api.deleteAlertRule(r.id).then(() => rules.refetch());
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      <section>
        <strong className="small">Channels</strong>
        {channels.data ? (
          <div className="card" style={{ marginTop: 8 }}>
            <table className="grid">
              <thead>
                <tr>
                  <th>Channel</th>
                  <th>Type</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {channels.data.map((c) => (
                  <tr key={c.id}>
                    <td>{c.label}</td>
                    <td className="mono small">{c.type}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {c.type !== 'in_app' ? (
                        <button className="btn small" onClick={() => void testChannel(c)}>
                          Send test
                        </button>
                      ) : (
                        <span className="faint small">always on</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        <p className="faint small" style={{ marginTop: 6 }}>
          Email and webhook channels are configured via the API for now; the in-app feed is always active.
        </p>
      </section>

      {editRule && rules.data ? (
        <RuleModal
          rule={editRule === 'new' ? null : editRule}
          kinds={rules.data.kinds}
          onClose={() => setEditRule(null)}
          onSaved={() => {
            setEditRule(null);
            rules.refetch();
          }}
        />
      ) : null}
    </div>
  );
}

function RuleModal({
  rule,
  kinds,
  onClose,
  onSaved,
}: {
  rule: AlertRule | null;
  kinds: string[];
  onClose: () => void;
  onSaved: () => void;
}): ReactNode {
  const [label, setLabel] = useState(rule?.label ?? '');
  const [kind, setKind] = useState(rule?.kind ?? kinds[0] ?? 'display_offline');
  const [severity, setSeverity] = useState<AlertSeverity>(rule?.severity ?? 'warning');
  const [paramsText, setParamsText] = useState(JSON.stringify(rule?.params ?? {}, null, 2));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  async function save(): Promise<void> {
    setBusy(true);
    setError(undefined);
    let params: unknown;
    try {
      params = JSON.parse(paramsText || '{}');
    } catch {
      setError('Parameters must be valid JSON.');
      setBusy(false);
      return;
    }
    try {
      await api.saveAlertRule(rule?.id ?? null, { label, kind, severity, params });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <Modal title={rule ? `Edit ${rule.label}` : 'New rule'} onClose={onClose}>
      <div className="stack">
        <label className="field">
          Name
          <input value={label} onChange={(e) => setLabel(e.target.value)} autoFocus />
        </label>
        <label className="field">
          Kind
          <select value={kind} onChange={(e) => setKind(e.target.value)} disabled={!!rule}>
            {kinds.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Severity
          <select value={severity} onChange={(e) => setSeverity(e.target.value as AlertSeverity)}>
            <option value="info">info</option>
            <option value="warning">warning</option>
            <option value="critical">critical</option>
          </select>
        </label>
        <label className="field">
          Parameters (JSON)
          <textarea
            value={paramsText}
            onChange={(e) => setParamsText(e.target.value)}
            rows={4}
            className="mono"
          />
        </label>
        {error ? <div className="form-error">{error}</div> : null}
        <div className="row between">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={busy || label.trim() === ''} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
